import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  updateNotificationReadStatus,
  setNotificationConnectionStatus,
  setNotificationWebSocketError,
} from '../slices/notificationSlice'
import {
  NotificationDisplayType,
  NotificationVariant,
} from '../types/notifications'
import WebSocketManager from '@/services/websocketManager'
import { AppDispatch, RootState } from '../store'

const wsManager = WebSocketManager.getInstance()

export const initializeNotifications = createAsyncThunk<
  void,
  {
    jwtToken: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/initializeNotifications',
  async ({ jwtToken }, { rejectWithValue }) => {
    try {
      return await wsManager.connectToNotification(jwtToken)
    } catch (error) {
      return rejectWithValue(
        (error as Error).message ||
          'Failed to connect to notification websocket'
      )
    }
  }
)

export const markNotificationAsRead = createAsyncThunk<
  void,
  {
    notificationId: number
    type_code?: number
    displayType?: NotificationDisplayType
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/markNotificationAsRead',
  async (
    { notificationId, type_code, displayType },
    { rejectWithValue, dispatch }
  ) => {
    dispatch(updateNotificationReadStatus(notificationId))
    const success = wsManager.sendMessage('notifications', {
      action: 'mark_read',
      notification_id: notificationId,
      type_code: type_code,
      display_type: displayType,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const sendSystemMessage = createAsyncThunk<
  void,
  {
    roomId: string
    message: string
    displayType: NotificationDisplayType
    variant: NotificationVariant
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/sendSystemMessage',
  async ({ roomId, message, displayType, variant }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('notifications', {
      action: 'system_message',
      room_id: roomId,
      message: message.trim(),
      display_type: displayType,
      variant: variant,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const markAllNotificationsAsRead = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/markAllNotificationsAsRead', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('notifications', {
    action: 'mark_read',
  })

  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const disconnectNotifications = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('websocket/disconnectNotifications', async (_, { dispatch }) => {
  try {
    await wsManager.disconnect('notifications')
    dispatch(setNotificationConnectionStatus(false))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error disconnecting notification WebSocket'
    dispatch(setNotificationWebSocketError(errorMessage))
  }
})
